import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  ResultFooter,
  WindowLine,
} from "../components/filters/ConsoleFilterContract";
import { TemplateColorChip } from "../components/TemplateColorChip";
import {
  fetchScheduleCoverageExceptions,
  scheduleCoverageCopy,
  scheduleCoverageExceptionCopy,
  type ScheduleCoverageExceptions,
  type ScheduleCoverageRow,
} from "../data/schedule-coverage-exceptions";
import {
  fetchServiceWeekTargets,
  type ServiceWeekTargets,
} from "../data/service-week-approval";
import {
  addServiceWeeks,
  currentServiceWeekStart,
  formatServiceWeekInstant,
  formatServiceWeekRange,
} from "../lib/service-week";
import { scheduleTimesheetsPath } from "../lib/schedule-timesheets-route";

type Target = Extract<ServiceWeekTargets, { status: "ok" }>["targets"][number];

export function ScheduleCoverage({
  client,
  accountId,
  scopeToken = "default",
  pollMs = 30000,
}: {
  client: SupabaseClient;
  accountId: string;
  /** Opaque account + support token; changes clear retained coverage facts (SC-17). */
  scopeToken?: string;
  timeZone?: string;
  pollMs?: number;
}) {
  const [targets, setTargets] = useState<ServiceWeekTargets | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [site, setSite] = useState<Target | null>(null);
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [load, setLoad] = useState<ScheduleCoverageExceptions | null>(null);
  const [loading, setLoading] = useState(true);
  const generation = useRef(0);

  const clearFacts = useCallback(() => {
    generation.current += 1;
    setLoad(null);
  }, []);

  const resetSelection = useCallback(() => {
    setClientId(null);
    setSite(null);
    setWeekStart(null);
  }, []);

  const loadTargets = useCallback(async () => {
    clearFacts();
    resetSelection();
    setTargets(null);
    setLoading(true);
    const request = generation.current;
    const next = await fetchServiceWeekTargets(client, accountId);
    if (request !== generation.current) return;
    setTargets(next);
    if (next.status === "ok" && next.targets.length > 0) {
      const first = next.targets[0]!;
      setClientId(first.clientId);
      setSite(first);
      setWeekStart(currentServiceWeekStart(first.timeZone));
    }
    setLoading(false);
  }, [accountId, clearFacts, client, resetSelection]);

  const loadCoverage = useCallback(async () => {
    if (site === null || weekStart === null) return;
    setLoading(true);
    const request = generation.current;
    const next = await fetchScheduleCoverageExceptions(client, {
      accountId,
      clientId: site.clientId,
      siteId: site.siteId,
      weekStart,
    });
    if (request !== generation.current) return;
    setLoad(next);
    setLoading(false);
  }, [accountId, client, site, weekStart]);

  useEffect(() => {
    void loadTargets();
  }, [loadTargets, scopeToken]);

  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  useEffect(() => {
    if (pollMs <= 0) return undefined;
    const timer = window.setInterval(() => {
      void loadCoverage();
    }, pollMs);
    return () => window.clearInterval(timer);
  }, [loadCoverage, pollMs]);

  const clients = useMemo(() => {
    if (targets?.status !== "ok") return [];
    const map = new Map<string, string>();
    for (const target of targets.targets) map.set(target.clientId, target.clientLabel);
    return [...map.entries()].map(([id, label]) => ({ id, label }));
  }, [targets]);

  const sites = useMemo(() => {
    if (targets?.status !== "ok" || clientId === null) return [];
    return targets.targets.filter((target) => target.clientId === clientId);
  }, [clientId, targets]);

  const onClientChange = (nextClientId: string) => {
    clearFacts();
    setClientId(nextClientId);
    if (targets?.status !== "ok") return;
    const nextSite = targets.targets.find((entry) => entry.clientId === nextClientId) ?? null;
    setSite(nextSite);
    if (nextSite) setWeekStart(currentServiceWeekStart(nextSite.timeZone));
  };

  const onSiteChange = (nextSiteId: string) => {
    clearFacts();
    const nextSite = sites.find((entry) => entry.siteId === nextSiteId) ?? null;
    setSite(nextSite);
    if (nextSite) setWeekStart(currentServiceWeekStart(nextSite.timeZone));
  };

  const shiftWeek = (delta: number) => {
    if (weekStart === null) return;
    const next = addServiceWeeks(weekStart, delta);
    if (next === null) return;
    clearFacts();
    setWeekStart(next);
  };

  if (loading && targets === null) {
    return <p className="surface__state" role="status">{scheduleCoverageCopy.loading}</p>;
  }

  if (targets?.status === "empty") {
    return (
      <p className="surface__state" role="status">{scheduleCoverageCopy.targetsEmpty}</p>
    );
  }

  if (targets?.status === "unavailable") {
    return (
      <div className="report__unavailable" role="status">
        <p className="report__state-kicker">Unavailable, not empty</p>
        <p>{scheduleCoverageCopy.unavailable}</p>
        <button type="button" onClick={() => { void loadTargets(); }}>Refresh</button>
      </div>
    );
  }

  const weekLabel = weekStart ? formatServiceWeekRange(weekStart) : null;

  return (
    <div className="schedule schedule-coverage">
      <div className="schedule-coverage__filters" role="group" aria-label="Coverage target">
        <label className="schedule-coverage__field">
          <span className="schedule-coverage__field-label">Client</span>
          <select
            aria-label="Client"
            value={clientId ?? ""}
            onChange={(event) => onClientChange(event.target.value)}
          >
            {clients.map((entry) => (
              <option key={entry.id} value={entry.id}>{entry.label}</option>
            ))}
          </select>
        </label>
        <label className="schedule-coverage__field">
          <span className="schedule-coverage__field-label">Site</span>
          <select
            aria-label="Site"
            value={site?.siteId ?? ""}
            onChange={(event) => onSiteChange(event.target.value)}
          >
            {sites.map((entry) => (
              <option key={entry.siteId} value={entry.siteId}>{entry.siteLabel}</option>
            ))}
          </select>
        </label>
        <div className="schedule-coverage__field">
          <span className="schedule-coverage__field-label" id="schedule-coverage-week-label">Week</span>
          <div className="schedule-coverage__week-nav" role="group" aria-labelledby="schedule-coverage-week-label">
            <button type="button" aria-label="Previous week" onClick={() => shiftWeek(-1)}>←</button>
            <span className="schedule-coverage__week-range">{weekLabel ?? ""}</span>
            <button type="button" aria-label="Next week" onClick={() => shiftWeek(1)}>→</button>
          </div>
        </div>
      </div>

      {site && weekStart ? (
        <WindowLine
          variant={{
            kind: "schedule_coverage",
            timeZone: site.timeZone,
            weekStart,
          }}
        />
      ) : null}

      <div className="schedule-coverage__results">
        {loading ? <p className="surface__state" role="status">{scheduleCoverageCopy.loading}</p> : null}

        {!loading && load?.status === "unavailable" ? (
          <div className="report__unavailable" role="status">
            <p className="report__state-kicker">Unavailable, not empty</p>
            <p>{scheduleCoverageCopy.unavailable}</p>
            <button type="button" onClick={() => { void loadCoverage(); }}>Refresh</button>
          </div>
        ) : null}

        {!loading && load?.status === "empty" ? (
          <p className="surface__state" role="status">{scheduleCoverageCopy.empty}</p>
        ) : null}

        {!loading && load?.status === "ok" ? (
          <>
            <p className="schedule__note" role="status">
              {load.weekEnded ? scheduleCoverageCopy.endedWeekLead : scheduleCoverageCopy.runningWeek}
            </p>
            {load.weekEnded ? (
              <p className="schedule__note">
                <Link to={scheduleTimesheetsPath({ clientId: site!.clientId, siteId: site!.siteId, weekStart: load.weekStart })}>
                  Open Schedule, Timesheets
                </Link>
              </p>
            ) : null}
            <CoverageTable rows={load.rows} timeZone={load.timeZone} />
            <ResultFooter
              state={load.rows.length === 0 ? "none" : "all"}
              foot={`${load.summary.exceptionCount} service exception${load.summary.exceptionCount === 1 ? "" : "s"} in this snapshot.`}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function CoverageTable({ rows, timeZone }: { rows: ScheduleCoverageRow[]; timeZone: string }) {
  if (rows.length === 0) return null;
  return (
    <div className="schedule-coverage__table-wrap">
      <table className="schedule__table schedule-coverage__table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Window</th>
            <th>State</th>
            <th>Exceptions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CoverageRow key={row.rowOrdinal} row={row} timeZone={timeZone} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoverageRow({ row, timeZone }: { row: ScheduleCoverageRow; timeZone: string }) {
  const token = row.kind === "scheduled_seat" ? row.templateStyleToken : "slate";
  const window =
    row.kind === "scheduled_seat"
      ? `${formatServiceWeekInstant(row.scheduledStart, timeZone) ?? row.scheduledStart} to ${formatServiceWeekInstant(row.scheduledEnd, timeZone) ?? row.scheduledEnd}`
      : "Unscheduled service";
  const state =
    row.kind === "unscheduled"
      ? row.disposition
      : row.fillState === "open" && row.openFillMode === "approval_required"
        ? scheduleCoverageCopy.openFillApprovalLabel
        : row.fillState === "open" && row.openFillMode === "instant_claim"
          ? scheduleCoverageCopy.openFillInstant
          : row.fillState;
  return (
    <tr className={`schedule__row schedule__row--keyed tpl-color--${token}`}>
      <td>
        {row.kind === "scheduled_seat" ? (
          <>
            <TemplateColorChip colorToken={row.templateStyleToken} />
            <span>{row.serviceLabel}</span>
            <span className="schedule__meta">{row.templateLabel}</span>
            <span className="schedule__meta">{row.seatLocator}</span>
          </>
        ) : (
          <span>{row.serviceLabel}</span>
        )}
      </td>
      <td>{window}</td>
      <td>{state}</td>
      <td>
        {row.exceptionCodes.map((code) => (
          <span key={code} className="schedule__tag">{scheduleCoverageExceptionCopy[code]}</span>
        ))}
      </td>
    </tr>
  );
}
